import {
  Calendar,
  ChevronDown,
  DollarSign,
  Eye,
  FileText,
  Image as ImageIcon,
  MousePointer,
  Target,
  Video,
} from 'lucide-react'
import { useState } from 'react'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import {
  googleAdsGetAssetDetails,
  type GoogleAdsGetAssetDetailsResult,
} from 'sprites-types/workflow/tools/googleAdsGetAssetDetails'
import { ObjectDisplay } from '../shared/ObjectDisplay'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

type AssetPerformanceItem = GoogleAdsGetAssetDetailsResult[number]

// Utility functions
const formatCurrency = (amount: number | null | undefined) => {
  if (amount == null) return 'N/A'
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount)
}

const formatNumber = (num: number | null | undefined) => {
  if (num == null) return 'N/A'
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`
  return num.toLocaleString()
}

const formatPercent = (value: number | null | undefined) => {
  if (value == null) return 'N/A'
  return `${value.toFixed(2)}%`
}

const getAssetTypeIcon = (type: string) => {
  switch (type) {
    case 'HEADLINE':
      return <FileText className="h-4 w-4 text-blue-500" />
    case 'DESCRIPTION':
      return <FileText className="h-4 w-4 text-green-500" />
    case 'IMAGE':
      return <ImageIcon className="h-4 w-4 text-purple-500" />
    case 'VIDEO':
      return <Video className="h-4 w-4 text-red-500" />
    case 'LOGO':
      return <ImageIcon className="h-4 w-4 text-orange-500" />
    default:
      return <FileText className="h-4 w-4 text-gray-500" />
  }
}

const getAssetTypeColor = (type: string) => {
  switch (type) {
    case 'HEADLINE':
      return 'bg-blue-100 text-blue-800'
    case 'DESCRIPTION':
      return 'bg-green-100 text-green-800'
    case 'IMAGE':
      return 'bg-purple-100 text-purple-800'
    case 'VIDEO':
      return 'bg-red-100 text-red-800'
    case 'LOGO':
      return 'bg-orange-100 text-orange-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

function AssetItem({ asset }: { asset: AssetPerformanceItem }) {
  const [isExpanded, setIsExpanded] = useState(false)
  const perf = asset.performance || {}

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="mb-2 flex items-center gap-2">
            {getAssetTypeIcon(asset.assetType)}
            <h4 className="font-medium text-gray-900">
              {asset.assetName || `Asset ${asset.assetId}`}
            </h4>
            <span
              className={`rounded-full px-2 py-0.5 text-xs font-medium ${getAssetTypeColor(asset.assetType)}`}
            >
              {asset.assetType}
            </span>
          </div>
          {asset.assetGroupName && (
            <p className="text-sm text-gray-600">Asset Group: {asset.assetGroupName}</p>
          )}
          {asset.campaignName && (
            <p className="text-sm text-gray-600">Campaign: {asset.campaignName}</p>
          )}
        </div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="ml-2 text-gray-400 hover:text-gray-600"
        >
          <ChevronDown
            className={`h-5 w-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          />
        </button>
      </div>

      {/* Performance Metrics Grid */}
      <div className="mt-3 grid grid-cols-3 gap-3">
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-2">
          <div className="mb-1 flex items-center gap-1">
            <Eye className="h-3 w-3 text-gray-600" />
            <span className="text-xs font-medium text-gray-700">Impressions</span>
          </div>
          <div className="text-base font-bold text-gray-900">{formatNumber(perf.impressions)}</div>
        </div>

        {perf.clicks != null && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-2">
            <div className="mb-1 flex items-center gap-1">
              <MousePointer className="h-3 w-3 text-gray-600" />
              <span className="text-xs font-medium text-gray-700">Clicks</span>
            </div>
            <div className="text-base font-bold text-gray-900">{formatNumber(perf.clicks)}</div>
          </div>
        )}

        {perf.cost != null && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-2">
            <div className="mb-1 flex items-center gap-1">
              <DollarSign className="h-3 w-3 text-gray-600" />
              <span className="text-xs font-medium text-gray-700">Cost</span>
            </div>
            <div className="text-base font-bold text-gray-900">{formatCurrency(perf.cost)}</div>
          </div>
        )}
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="mt-3 border-t border-gray-200 pt-3">
          <div className="grid grid-cols-2 gap-3">
            {perf.ctr != null && (
              <div className="text-sm">
                <span className="font-medium text-gray-700">CTR: </span>
                <span className="text-gray-900">{formatPercent(perf.ctr)}</span>
              </div>
            )}
            {perf.cpc != null && (
              <div className="text-sm">
                <span className="font-medium text-gray-700">CPC: </span>
                <span className="text-gray-900">{formatCurrency(perf.cpc)}</span>
              </div>
            )}
            {perf.cpm != null && (
              <div className="text-sm">
                <span className="font-medium text-gray-700">CPM: </span>
                <span className="text-gray-900">{formatCurrency(perf.cpm)}</span>
              </div>
            )}
            {perf.conversions != null && (
              <div className="text-sm">
                <span className="font-medium text-gray-700">Conversions: </span>
                <span className="text-gray-900">{formatNumber(perf.conversions)}</span>
              </div>
            )}
            {perf.conversionRate != null && (
              <div className="text-sm">
                <span className="font-medium text-gray-700">Conversion Rate: </span>
                <span className="text-gray-900">{formatPercent(perf.conversionRate)}</span>
              </div>
            )}
          </div>

          {/* Daily Breakdown */}
          {asset.dailyBreakdown && asset.dailyBreakdown.length > 0 && (
            <div className="mt-3">
              <div className="mb-2 flex items-center gap-1">
                <Calendar className="h-3 w-3 text-indigo-500" />
                <span className="text-xs font-medium text-gray-700">Daily Performance</span>
              </div>
              <div className="space-y-1">
                {asset.dailyBreakdown.slice(-7).map((day, index) => (
                  <div key={day.date || index} className="flex justify-between text-xs">
                    <span className="text-gray-600">{day.date}</span>
                    <div className="flex gap-3 text-gray-900">
                      <span>{formatNumber(day.impressions)} imp</span>
                      {day.clicks != null && <span>{formatNumber(day.clicks)} clicks</span>}
                      {day.cost != null && <span>{formatCurrency(day.cost)}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function GoogleAdsGetAssetDetailsDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const [showJson, setShowJson] = useState(false)
  const schema = getToolResultSchema(googleAdsGetAssetDetails, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data as GoogleAdsGetAssetDetailsResult

  if (showJson) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">Asset Details Results</h3>
          <button
            onClick={() => setShowJson(false)}
            className="text-xs text-blue-600 hover:text-blue-800"
          >
            View Formatted
          </button>
        </div>
        <ObjectDisplay data={result} />
      </div>
    )
  }

  if (!result || result.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <h3 className="text-sm font-semibold text-gray-900">Asset Details Results</h3>
        <p className="mt-2 text-sm text-gray-600">No asset performance data found.</p>
      </div>
    )
  }

  // Calculate totals and group by type
  const totals = result.reduce(
    (acc, asset) => {
      const perf = asset.performance || {}
      acc.impressions += perf.impressions || 0
      acc.clicks += perf.clicks || 0
      acc.cost += perf.cost || 0
      acc.conversions += perf.conversions || 0
      return acc
    },
    { impressions: 0, clicks: 0, cost: 0, conversions: 0 }
  )

  const byType = result.reduce(
    (acc, asset) => {
      const type = asset.assetType
      acc[type] = (acc[type] || 0) + 1
      return acc
    },
    {} as Record<string, number>
  )

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Asset Details Results</h3>
          <p className="text-xs text-gray-600">
            {result.length} asset{result.length !== 1 ? 's' : ''}
          </p>
        </div>
        <button
          onClick={() => setShowJson(true)}
          className="text-xs text-blue-600 hover:text-blue-800"
        >
          View JSON
        </button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-4 gap-3">
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
          <div className="mb-1 flex items-center gap-1">
            <Eye className="h-3 w-3 text-gray-600" />
            <span className="text-xs font-medium text-gray-700">Total Impressions</span>
          </div>
          <div className="text-lg font-bold text-gray-900">{formatNumber(totals.impressions)}</div>
        </div>
        {totals.clicks > 0 && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
            <div className="mb-1 flex items-center gap-1">
              <MousePointer className="h-3 w-3 text-gray-600" />
              <span className="text-xs font-medium text-gray-700">Total Clicks</span>
            </div>
            <div className="text-lg font-bold text-gray-900">{formatNumber(totals.clicks)}</div>
          </div>
        )}
        {totals.cost > 0 && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
            <div className="mb-1 flex items-center gap-1">
              <DollarSign className="h-3 w-3 text-gray-600" />
              <span className="text-xs font-medium text-gray-700">Total Cost</span>
            </div>
            <div className="text-lg font-bold text-gray-900">{formatCurrency(totals.cost)}</div>
          </div>
        )}
        {totals.conversions > 0 && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
            <div className="mb-1 flex items-center gap-1">
              <Target className="h-3 w-3 text-gray-600" />
              <span className="text-xs font-medium text-gray-700">Total Conversions</span>
            </div>
            <div className="text-lg font-bold text-gray-900">
              {formatNumber(totals.conversions)}
            </div>
          </div>
        )}
      </div>

      {/* Asset Type Breakdown */}
      {Object.keys(byType).length > 0 && (
        <div className="flex flex-wrap gap-2">
          {Object.entries(byType).map(([type, count]) => (
            <div
              key={type}
              className={`rounded-full px-3 py-1 text-xs font-medium ${getAssetTypeColor(type)}`}
            >
              {type}: {count}
            </div>
          ))}
        </div>
      )}

      {/* Assets List */}
      <div className="space-y-3">
        {result.map((asset, index) => (
          <AssetItem key={asset.assetId || index} asset={asset} />
        ))}
      </div>
    </div>
  )
}

async function downloadGoogleAdsAssetPerformance(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function googleAdsGetAssetDetailsToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(googleAdsGetAssetDetails, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data
  return `## Google Ads Asset Details\n\n\`\`\`json\n${JSON.stringify(result, null, 2)}\n\`\`\`\n\n---`
}

export const GoogleAdsGetAssetDetailsDisplay = createWorkflowToolComponent(
  GoogleAdsGetAssetDetailsDisplayComponent,
  downloadGoogleAdsAssetPerformance,
  googleAdsGetAssetDetailsToMarkdown
)
